/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A startup failure is VISIBLE ON THE PAGE. It never renders nothing.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * This exists because of a mistake made on C0's first afternoon, which is worth keeping
 * written down: the configuration check ran *before* React mounted and threw on a missing
 * variable, so a misconfigured dev server served a **blank white page** with the
 * explanation only in the console.
 *
 * That is the single failure mode this project has paid for most often — a page that is a
 * 200 with an empty `#root` and no visible cause — and it was reintroduced by a guard
 * written to prevent it. Refusing loudly is right; refusing invisibly is not.
 *
 * ⚠️ **Deliberately dependency-free**: plain DOM, no React, no tokens, no CSS imports.
 * Whatever went wrong may be the thing that stops React or the stylesheet loading, so the
 * one surface that has to survive it cannot rely on either.
 */
export function renderStartupFailure(root: HTMLElement, error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);

  root.textContent = '';

  const box = document.createElement('div');
  box.setAttribute('data-startup-failure', '');
  box.setAttribute('role', 'alert');
  box.style.cssText =
    'font:14px/1.6 ui-sans-serif,system-ui,sans-serif;max-width:56ch;margin:12vh auto;padding:0 24px;color:#2a2a33';

  const title = document.createElement('h1');
  title.style.cssText = 'font-size:17px;margin:0 0 8px';
  title.textContent = 'Az alkalmazás nem indult el';

  const detail = document.createElement('p');
  detail.style.cssText = 'margin:0 0 12px;white-space:pre-wrap';
  detail.textContent = message;

  const hint = document.createElement('p');
  hint.style.cssText = 'margin:0;color:#6b6b78';
  hint.textContent = 'Ez beállítási hiba, nem hálózati. A részletek a konzolban is megvannak.';

  box.append(title, detail, hint);
  root.append(box);
}
